// Procedural SFX. Every event is synthesized at play time — no audio files.
// Each "play(event)" composes oscillators + gain envelopes + (optionally)
// noise bursts to produce a recognizable pirate-flavored cue.
//
// Design goals:
//  - Tiny: zero bytes of asset.
//  - Distinct: each event produces a different timbre.
//  - Cheap: single-shot, garbage-collected after the envelope finishes.
//  - Mobile-safe: respects the AudioContext lock state via mixer.ensure().
import { mixer } from './mixer';
const NOTE = {
    C3: 130.81, D3: 146.83, E3: 164.81, F3: 174.61, G3: 196.0, A3: 220.0, B3: 246.94,
    C4: 261.63, D4: 293.66, E4: 329.63, F4: 349.23, G4: 392.0, A4: 440.0, B4: 493.88,
    C5: 523.25, D5: 587.33, E5: 659.25, F5: 698.46, G5: 783.99, A5: 880.0, B5: 987.77,
    C6: 1046.5, E6: 1318.5, G6: 1568.0,
};
export function play(event) {
    const ctx = mixer.ensure();
    if (!ctx || mixer.muted)
        return;
    const out = mixer.out();
    if (!out)
        return;
    const t = ctx.currentTime;
    switch (event) {
        case 'card_play':
            bleepEnv(ctx, out, NOTE.E5, 0.08, 'square', 0.18, t);
            break;
        case 'card_draw':
            bleepEnv(ctx, out, NOTE.G5, 0.06, 'sine', 0.12, t);
            break;
        case 'card_flip':
            whoosh(ctx, out, 0.16, 0.16, t);
            break;
        case 'card_toss':
            noiseBurst(ctx, out, 0.16, 0.18, 800, t);
            break;
        case 'coin_earn_small':
            bleepEnv(ctx, out, NOTE.B5, 0.08, 'triangle', 0.18, t);
            bleepEnv(ctx, out, NOTE.D5, 0.08, 'triangle', 0.16, t + 0.04);
            break;
        case 'coin_earn_large':
            arpeggio(ctx, out, [NOTE.E5, NOTE.G5, NOTE.B5, NOTE.E6], 0.07, 0.05, 'triangle', t);
            break;
        case 'coin_steal':
            // Sharper coin-clink with descending sting
            arpeggio(ctx, out, [NOTE.E6, NOTE.B5, NOTE.G5], 0.06, 0.04, 'square', t, 0.18);
            break;
        case 'coin_sell':
            arpeggio(ctx, out, [NOTE.D5, NOTE.A4], 0.10, 0.06, 'triangle', t);
            break;
        case 'pattern_double':
            arpeggio(ctx, out, [NOTE.C5, NOTE.E5], 0.08, 0.05, 'triangle', t);
            break;
        case 'pattern_triple':
            arpeggio(ctx, out, [NOTE.C5, NOTE.E5, NOTE.G5], 0.08, 0.05, 'triangle', t);
            break;
        case 'pattern_sandwich':
            arpeggio(ctx, out, [NOTE.G5, NOTE.E5, NOTE.G5], 0.08, 0.05, 'square', t);
            break;
        case 'pattern_marriage':
            arpeggio(ctx, out, [NOTE.C5, NOTE.E5, NOTE.G5, NOTE.C6], 0.10, 0.06, 'triangle', t);
            break;
        case 'pattern_run':
            arpeggio(ctx, out, [NOTE.C5, NOTE.D5, NOTE.E5, NOTE.F5, NOTE.G5], 0.09, 0.04, 'triangle', t);
            break;
        case 'pattern_suit_chain':
            arpeggio(ctx, out, [NOTE.E5, NOTE.G5, NOTE.B5], 0.10, 0.05, 'sine', t);
            break;
        case 'pattern_flush':
            // 5-card flush — fancy upward sweep
            arpeggio(ctx, out, [NOTE.E5, NOTE.A5, NOTE.E6, NOTE.A5, NOTE.E6], 0.07, 0.05, 'triangle', t);
            break;
        case 'pattern_straight_flush':
            arpeggio(ctx, out, [NOTE.C5, NOTE.E5, NOTE.G5, NOTE.C6, NOTE.E6, NOTE.G6], 0.08, 0.045, 'triangle', t);
            break;
        case 'pattern_royalty':
            // Regal triad sustained
            sustainedChord(ctx, out, [NOTE.C5, NOTE.E5, NOTE.G5], 0.45, 'triangle', t, 0.16);
            break;
        case 'pattern_mutiny':
            // Cannon boom + triumph
            cannonBoom(ctx, out, t);
            arpeggio(ctx, out, [NOTE.E5, NOTE.A5, NOTE.E6], 0.10, 0.06, 'square', t + 0.18);
            break;
        case 'pattern_parity':
            arpeggio(ctx, out, [NOTE.D5, NOTE.F5, NOTE.A5], 0.08, 0.04, 'triangle', t);
            break;
        case 'hull_hit':
            cannonBoom(ctx, out, t, 0.6);
            break;
        case 'hull_low':
            // Distorted low rumble
            bleepEnv(ctx, out, NOTE.A3, 0.5, 'sawtooth', 0.10, t);
            break;
        case 'ship_sink':
            whoosh(ctx, out, 1.2, 0.3, t);
            bleepEnv(ctx, out, NOTE.C3, 1.2, 'sawtooth', 0.18, t + 0.1);
            break;
        case 'button_click':
            bleepEnv(ctx, out, NOTE.A4, 0.04, 'square', 0.10, t);
            break;
        case 'modal_open':
            bleepEnv(ctx, out, NOTE.E5, 0.08, 'sine', 0.12, t);
            bleepEnv(ctx, out, NOTE.G5, 0.08, 'sine', 0.10, t + 0.04);
            break;
        case 'modal_close':
            bleepEnv(ctx, out, NOTE.G4, 0.08, 'sine', 0.10, t);
            break;
        case 'shop_buy':
            arpeggio(ctx, out, [NOTE.A4, NOTE.C5, NOTE.E5], 0.07, 0.04, 'triangle', t);
            break;
        case 'shop_refresh':
            whoosh(ctx, out, 0.25, 0.15, t);
            break;
        case 'win_fanfare':
            arpeggio(ctx, out, [NOTE.C5, NOTE.E5, NOTE.G5, NOTE.C6, NOTE.E6, NOTE.G6, NOTE.C6], 0.18, 0.10, 'triangle', t, 0.22);
            break;
        case 'lose_fanfare':
            arpeggio(ctx, out, [NOTE.E4, NOTE.D4, NOTE.C4, NOTE.A3, NOTE.E3], 0.30, 0.18, 'sawtooth', t, 0.18);
            break;
        case 'ante_clear':
            arpeggio(ctx, out, [NOTE.G5, NOTE.B5, NOTE.D5, NOTE.G5], 0.10, 0.06, 'square', t, 0.18);
            break;
        case 'cannon_fire':
            // Quick, sharp cannon shot — punchier than the deep mutiny boom
            cannonShot(ctx, out, t, 0.7);
            break;
        case 'cannon_impact':
            // Wood splintering / debris — short noise burst at lower freq
            noiseBurst(ctx, out, 0.18, 0.22, 600, t);
            break;
    }
}
function cannonShot(ctx, out, startAt, scale = 1) {
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = 'sine';
    o.frequency.setValueAtTime(180, startAt);
    o.frequency.exponentialRampToValueAtTime(70, startAt + 0.08);
    g.gain.setValueAtTime(0, startAt);
    g.gain.linearRampToValueAtTime(0.32 * scale, startAt + 0.005);
    g.gain.exponentialRampToValueAtTime(0.001, startAt + 0.18);
    o.connect(g).connect(out);
    o.start(startAt);
    o.stop(startAt + 0.22);
    noiseBurst(ctx, out, 0.12, 0.22 * scale, 2200, startAt);
}
// ── Synthesis primitives ────────────────────────────────────────────
function bleepEnv(ctx, out, freq, durSec, type, peak, startAt) {
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = type;
    o.frequency.value = freq;
    g.gain.setValueAtTime(0, startAt);
    g.gain.linearRampToValueAtTime(peak, startAt + 0.005);
    g.gain.exponentialRampToValueAtTime(0.001, startAt + durSec);
    o.connect(g).connect(out);
    o.start(startAt);
    o.stop(startAt + durSec + 0.02);
}
function arpeggio(ctx, out, freqs, noteDur, noteSpacing, type, startAt, peak = 0.18) {
    freqs.forEach((f, i) => bleepEnv(ctx, out, f, noteDur, type, peak, startAt + i * noteSpacing));
}
function sustainedChord(ctx, out, freqs, durSec, type, startAt, peak) {
    for (const f of freqs)
        bleepEnv(ctx, out, f, durSec, type, peak / freqs.length, startAt);
}
function noiseBurst(ctx, out, durSec, peak, filterFreq, startAt) {
    const buf = ctx.createBuffer(1, Math.ceil(ctx.sampleRate * durSec), ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++)
        data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const filt = ctx.createBiquadFilter();
    filt.type = 'lowpass';
    filt.frequency.value = filterFreq;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, startAt);
    g.gain.linearRampToValueAtTime(peak, startAt + 0.01);
    g.gain.exponentialRampToValueAtTime(0.001, startAt + durSec);
    src.connect(filt).connect(g).connect(out);
    src.start(startAt);
    src.stop(startAt + durSec + 0.02);
}
function whoosh(ctx, out, durSec, peak, startAt) {
    const buf = ctx.createBuffer(1, Math.ceil(ctx.sampleRate * durSec), ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++)
        data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const filt = ctx.createBiquadFilter();
    filt.type = 'bandpass';
    filt.frequency.setValueAtTime(2000, startAt);
    filt.frequency.exponentialRampToValueAtTime(400, startAt + durSec);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, startAt);
    g.gain.linearRampToValueAtTime(peak, startAt + 0.02);
    g.gain.exponentialRampToValueAtTime(0.001, startAt + durSec);
    src.connect(filt).connect(g).connect(out);
    src.start(startAt);
    src.stop(startAt + durSec + 0.02);
}
function cannonBoom(ctx, out, startAt, scale = 1) {
    // Low-frequency thump + noise burst
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = 'sine';
    o.frequency.setValueAtTime(120, startAt);
    o.frequency.exponentialRampToValueAtTime(45, startAt + 0.15);
    g.gain.setValueAtTime(0, startAt);
    g.gain.linearRampToValueAtTime(0.45 * scale, startAt + 0.01);
    g.gain.exponentialRampToValueAtTime(0.001, startAt + 0.4);
    o.connect(g).connect(out);
    o.start(startAt);
    o.stop(startAt + 0.45);
    noiseBurst(ctx, out, 0.25, 0.30 * scale, 1500, startAt);
}
